import type { ComponentType } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { useThemeColors } from '../hooks/useThemeColors';
import type { ThemeColors } from '../theme/colors';
import { ICON_REGISTRY, type IconName } from './icon-registry';

export type { IconName };
export type IconSize = 'xs' | 'sm' | 'md' | 'lg';
export type IconTone =
  | 'default'
  | 'subdued'
  | 'accent'
  | 'accentGreen'
  | 'accentYellow'
  | 'accentOrange'
  | 'accentCrimson'
  | 'error'
  | 'success'
  | 'warning'
  | 'inverse';

const SIZE_PX: Record<IconSize, number> = {
  xs: 14,
  sm: 18,
  md: 24,
  lg: 32,
};

const TONE_TOKENS: Record<IconTone, keyof ThemeColors> = {
  default: 'textOnSurface',
  subdued: 'textSubdued',
  accent: 'accentCerulean',
  accentGreen: 'accentGreen',
  accentYellow: 'accentYellow',
  accentOrange: 'accentOrange',
  accentCrimson: 'accentCrimson',
  error: 'textError',
  success: 'textSuccess',
  warning: 'textWarning',
  inverse: 'textInverse',
};

export interface IconProps {
  /** Semantic icon name. Typed against the icon registry (`ICON_REGISTRY`). */
  name: IconName;
  /** xs=14, sm=18, md=24 (default), lg=32. Numeric override allowed for one-off needs. */
  size?: IconSize | number;
  /** Semantic color role resolved via `useThemeColors()`. Defaults to `default`. */
  tone?: IconTone;
  /** Direct color override — for branded/gradient cases only. Wins over `tone`. */
  color?: string;
  /** Wrapper style passthrough (used to position the icon when needed). */
  style?: StyleProp<ViewStyle>;
  /**
   * Optional a11y label. Icons are decorative by default and announce nothing.
   * Set this when the icon stands in for text (no adjacent label).
   */
  accessibilityLabel?: string;
}

/**
 * Theme-aware icon. The only sanctioned way to render an icon in `libs/ui`
 * and `apps/mobile`. Resolves size + tone via tokens so an icon's color
 * shifts correctly between light and dark mode and follows ADR-0072 state
 * tokens (error / success / warning tones).
 *
 * Backed by lucide (SVG, tree-shaken) via `icon-registry.ts` — swapping the
 * icon library is a registry-only change; call sites never import a library
 * directly.
 *
 * Decorative usage (default): leave `accessibilityLabel` unset — the icon is
 * hidden from assistive tech. Standalone usage (no adjacent text label):
 * pass `accessibilityLabel` so the icon is announced.
 */
export function Icon({
  name,
  size = 'md',
  tone = 'default',
  color,
  style,
  accessibilityLabel,
}: IconProps) {
  const theme = useThemeColors();
  const resolvedSize = typeof size === 'number' ? size : SIZE_PX[size];
  const resolvedColor = color ?? (theme[TONE_TOKENS[tone]] as string);

  // lucide icon components forward extra props (style, a11y) to the
  // underlying react-native-svg <Svg>. Typed loosely here so the registry's
  // shared component shape doesn't fight RN's accessibility prop unions.
  const Glyph = ICON_REGISTRY[name] as ComponentType<Record<string, unknown>> | undefined;
  // `name` is typed against the registry, so this is unreachable through the
  // type system — but a value forced past the types (cast, persisted/stale
  // data) would otherwise render `<undefined>` and white-screen the app.
  // Guard it: warn in dev, render nothing in prod.
  if (!Glyph) {
    if (__DEV__) console.warn(`[Icon] unknown icon name: "${String(name)}"`);
    return null;
  }

  const props: Record<string, unknown> = { size: resolvedSize, color: resolvedColor };
  if (style) props.style = style;
  if (accessibilityLabel) {
    props.accessibilityLabel = accessibilityLabel;
    props.accessibilityRole = 'image';
  } else {
    props.accessibilityElementsHidden = true;
    props.importantForAccessibility = 'no-hide-descendants';
  }

  return <Glyph {...props} />;
}
